/**
 * Plans shown in the Platform, derived from the offer catalogue.
 *
 * offerCatalog.json is a byte-identical copy of the backend catalogue
 * (b2b-phase2-node-backend/config/offerCatalog.json; check with
 * scripts/sync_offer_catalog.js). Prices, capacity and feature tiers come from
 * it; this file only adds display copy. Every amount charged is computed by
 * the backend (/subscription/quote), never in the browser.
 *
 * Technical tiers: 'solo' | 'pro' (= Team) | 'business'.
 */
import catalog from './offerCatalog.json';

export const OFFER = catalog;

// Self-serve checkout shows prices and a Buy button only when Stripe carries
// exactly the catalogue prices (checked by scripts/verify_stripe_pricing.js);
// otherwise the plan cards ask for a proposal.
export const SELF_SERVE_CHECKOUT_ENABLED = process.env.REACT_APP_SELF_SERVE_CHECKOUT !== 'false';

const COPY = {
  solo: {
    audience: 'An independent adviser who can authorise the use of their clients’ data',
    job: 'Prepare and follow up your own client reviews',
    result: 'A personal list of clients to review, the evidence available for each, your recorded actions and outcomes, and a PDF/CSV report.',
    features: [
      'Import of your client extract, checked before anything is saved',
      'Review list ranked on the evidence your data supports',
      'Notes, actions, next contact and recorded outcomes',
      'Per-client PDF report and CSV exports',
      'Your firm’s product catalogue, when you provide it',
      'Client data export and erasure on request',
    ],
    support: 'Asynchronous support, template and guide.',
  },
  team: {
    audience: 'The owner or operations lead of an advisory firm',
    job: 'Coordinate the review work of several advisers',
    result: 'One shared workspace: clients are assigned, each adviser works their own book, and the lead sees what was done and with what outcome.',
    features: [
      'Everything in Solo',
      'Shared workspace with Admin and Relationship Manager roles',
      'Assign and reassign clients to advisers',
      'Each adviser’s queue, actions and outcomes visible to the lead',
    ],
    support: 'Asynchronous support. Optional assisted onboarding: €290 one-off (up to 3 hours).',
  },
  business: {
    audience: 'The head of advice or control of a firm that oversees the whole book',
    job: 'Check consistency and evidence across the whole client book',
    result: 'Book-wide cases to review — holdings outside the client’s profile, advice without evidence in the extract, drift outside model bands — with euro amounts on eligible positions, coverage and reasons.',
    features: [
      'Everything in Team',
      'Book-wide signals with euro amounts on eligible EUR positions',
      'Head of Advisory, Compliance, Portfolio Adviser and View Only roles',
      'Audit console: search, activity and log export',
    ],
    support: 'Asynchronous support. Optional assisted onboarding: €590 one-off (up to 6 hours).',
    note: 'Amounts identify positions or transactions to review. They are not avoided losses or recoverable revenue.',
  },
  enterprise: {
    audience: 'Banks, networks and partners with a sponsor and budget',
    job: 'Run Business capabilities inside your own systems',
    result: 'An agreed data exchange, identity and customisation, with operational responsibilities defined in the contract.',
    features: [
      'Business capabilities within the agreed scope',
      'Data exchange with your systems, scoped as a project',
      'Company sign-in on your identity provider, built and verified for you',
      'Custom domain or embedded experience, as a project',
      'Contracted operational requirements and support',
    ],
    support: 'Scope, service levels and responsibilities are defined in the contract.',
  },
};

export const DATA_NEEDED = 'Client profiles and allocations in our CSV/XLSX template. Optional: positions, transactions, suitability evidence, model portfolios and your product catalogue. Indicators appear only where your data supports them.';

const selfServe = (id) => ({ ...catalog.plans[id], ...COPY[id], key: id });

export const PLANS = {
  solo: selfServe('solo'),
  team: selfServe('team'),
  business: { ...selfServe('business'), capacityPackage: catalog.plans.business_capacity },
  enterprise: {
    key: 'enterprise',
    name: catalog.enterprise.name,
    tier: null,
    price: null,
    ...COPY.enterprise,
    licenseFromYearly: catalog.enterprise.licenseFromYearly,
    onboardingFrom: catalog.enterprise.onboardingFrom,
  },
};

export const PLAN_ORDER = ['solo', 'team', 'business', 'enterprise'];

export const TIER_TO_PLAN = { solo: 'solo', pro: 'team', business: 'business' };

// Plan behind a technical tier ('pro' is Team).
export const planForBillingTier = (tier) => PLANS[TIER_TO_PLAN[tier]] || null;

export const hasFeature = (tier, feature) => Boolean(catalog.features[feature]?.includes(tier));

export const formatEuro = (amount) => `€${Number(amount).toLocaleString('en-US')}`;

export const formatListPrice = (plan, cycle = 'monthly') => {
  if (!plan?.price) return 'Quoted after scoping';
  return `${formatEuro(plan.price[cycle])} ${cycle === 'monthly' ? '/ month' : '/ year'}`;
};

export const capacityLabel = (plan) => {
  if (!plan?.included) return null;
  const people = plan.included.seats === 1 ? '1 person' : `${plan.included.seats} people`;
  const clients = `${plan.included.customers.toLocaleString('en-US')} active clients`;
  if (plan.max.seats === plan.included.seats && plan.max.customers === plan.included.customers) return `${people} · ${clients}`;
  return `${people} · ${clients} included (up to ${plan.max.seats} people / ${plan.max.customers.toLocaleString('en-US')} clients)`;
};

// Workspace plan label from the backend entitlement (legacy plans included).
export const workspacePlanLabel = (user) => {
  const entitlement = user?.entitlement;
  if (entitlement?.name) return entitlement.name;
  return planForBillingTier(user?.subscription)?.name || null;
};

export const CONNECTED_ADDON = {
  key: 'connected',
  name: 'Client Companion (Connected)',
  status: 'not_generally_available',
  summary: 'A named pilot with a sponsor, quoted separately. Not included in any subscription.',
};
